import {AttendanceStatusKey, STATUS_RING_ORDER, normalizeStatusKey} from './attendanceStatusTheme'
import {getV2ResultTableData} from './attendanceV2ResultTable'
import {AttendanceStats, AttendanceSummary, AttendanceV2Payload} from './attendanceV2Types'
import {
  AttendanceStatusSegment,
  AttendanceVisualSummary,
  buildStatusSegmentsFromRows,
} from './buildAttendanceVisualSummary'

function hasMeaningfulStatusCounts(summary: AttendanceSummary): boolean {
  return (
    (summary.present ?? 0) > 0 ||
    (summary.absent ?? 0) > 0 ||
    (summary.late ?? 0) > 0 ||
    (summary.leave ?? 0) > 0 ||
    (summary.bunk ?? 0) > 0 ||
    (summary.other ?? 0) > 0 ||
    (summary.totalStudents ?? 0) > 0
  )
}
function isPresentKey(key: AttendanceStatusKey): boolean {
  return key === 'present' || key === 'excused'
}

/** A chart needs at least one real mark to draw. An answer whose only segment is "unmarked" — a
 *  class with no enrolled students, a day nobody was marked — used to draw a full grey ring reading
 *  "1 unmarked" with a lone Unmarked bar beside it: a chart of the absence of information, under a
 *  narrative that had just said there was none. `countsTowardRate` is already how the summary and
 *  bucket builders tag the segments that are not attendance; row-derived segments carry no flag, so
 *  the `unmark` status is named as well. */
function hasMarkedSegments(segments: AttendanceStatusSegment[]): boolean {
  return segments.some((s) => s.count > 0 && s.countsTowardRate !== false && s.key !== 'unmark')
}

function summaryToStatusSegments(summary: AttendanceSummary): AttendanceStatusSegment[] {
  // `other` is the backend's `unmarked` — people the query returned who carry no mark at all. It is
  // worth showing, but it is not a kind of attendance and the rate is not taken over it.
  const counts: Array<{
    key: AttendanceStatusKey
    label: string
    count: number
    countsTowardRate?: boolean
  }> = [
    {key: 'present', label: 'Present', count: summary.present ?? 0},
    {key: 'absent', label: 'Absent', count: summary.absent ?? 0},
    {key: 'late', label: 'Late', count: summary.late ?? 0},
    {key: 'leave', label: 'Leave', count: summary.leave ?? 0},
    {key: 'bunk', label: 'Bunk', count: summary.bunk ?? 0},
    {key: 'other', label: 'Unmarked', count: summary.other ?? 0, countsTowardRate: false},
  ]

  const toSegment = (entry: (typeof counts)[number]): AttendanceStatusSegment => ({
    key: entry.key,
    label: entry.label,
    count: entry.count,
    countsTowardRate: entry.countsTowardRate ?? true,
  })

  const segments: AttendanceStatusSegment[] = []
  for (const key of STATUS_RING_ORDER) {
    const entry = counts.find((c) => c.key === key && c.count > 0)
    if (entry) segments.push(toSegment(entry))
  }
  for (const entry of counts) {
    if (entry.count > 0 && !segments.some((s) => s.key === entry.key)) {
      segments.push(toSegment(entry))
    }
  }

  return segments
}

/** `authoritativePercent` is the backend's own `stats.attendancePercent` — attended (present + late)
 *  over the marks it actually counted. Recomputing here instead put a second, different number beside
 *  the same answer: the ring divided present-only by every row INCLUDING the unmarked ones, so a day
 *  the narration called "86.3% attendance" drew as 80% (96/120 rather than 101/117) — disagreeing on
 *  both the numerator and the denominator. The backend tally is the single source of truth, so it
 *  wins whenever supplied; the local calculation remains the fallback for row-derived segments, where
 *  no tally exists.
 *
 *  `authoritativePercent` therefore carries three states: a number (the backend stated a rate — use
 *  it), `null` (the backend suppressed the rate — show none, derive nothing), and `undefined` (no
 *  tally was supplied at all — derive locally). */
function buildVisualFromSegments(
  statusSegments: AttendanceStatusSegment[],
  totalRecords: number,
  authoritativePercent?: number | null
): AttendanceVisualSummary {
  const presentCount = statusSegments
    .filter((s) => isPresentKey(s.key))
    .reduce((sum, s) => sum + s.count, 0)
  // The fallback has to measure the same thing the label claims. It divided present-only by every
  // segment INCLUDING the unmarked ones — two departures from the rate the backend and the
  // narration both quote, which credits Late as attended and counts only the marks that exist.
  const attendedCount = statusSegments
    .filter((s) => isPresentKey(s.key) || s.key === 'late')
    .reduce((sum, s) => sum + s.count, 0)
  const countable = statusSegments
    .filter((s) => s.countsTowardRate !== false)
    .reduce((sum, s) => sum + s.count, 0)
  // One decimal, matching the backend's `attendancePercent` and the figure the
  // narration quotes. Rounding to a whole number here printed 66% in the ring
  // beside text correctly saying 65.5% — the same number disagreeing with itself
  // because two places rounded it differently. The backend has already rounded
  // to one decimal, so the authoritative value is passed through untouched.
  const derivedPercent = countable > 0 ? Math.round((attendedCount / countable) * 1000) / 10 : null
  // Three states, not two. `undefined` means no tally was supplied at all (segments built from table
  // rows), so derive one. `null` means a tally WAS supplied and the backend suppressed the rate on
  // purpose — deriving it here recomputes the exact figure it refused to state, which for
  // status-filtered rows is 100% off its own WHERE clause.
  const presentPercent =
    authoritativePercent === undefined
      ? derivedPercent
      : Number.isFinite(authoritativePercent as number)
        ? (authoritativePercent as number)
        : null

  return {
    mode: 'daily',
    totalRecords,
    statusSegments,
    presentCount,
    presentPercent,
    dayEntries: [],
    statPairs: [],
  }
}

export function v2SummaryToVisualSummary(summary: AttendanceSummary): AttendanceVisualSummary {
  const statusSegments = summaryToStatusSegments(summary)
  if (statusSegments.length > 0) {
    return buildVisualFromSegments(
      statusSegments,
      summary.total ?? statusSegments.reduce((s, x) => s + x.count, 0),
      summary.presentPercent
    )
  }

  return {
    mode: 'stats',
    totalRecords: summary.total ?? 0,
    statusSegments: [],
    presentCount: summary.present ?? 0,
    presentPercent: summary.presentPercent ?? null,
    dayEntries: [],
    statPairs: [
      {metric: 'Present', value: String(summary.present ?? 0)},
      {metric: 'Absent', value: String(summary.absent ?? 0)},
      {metric: 'Late', value: String(summary.late ?? 0)},
      {metric: 'Leave', value: String(summary.leave ?? 0)},
      {metric: 'Bunk', value: String(summary.bunk ?? 0)},
      {metric: 'Other', value: String(summary.other ?? 0)},
      {metric: 'Total', value: String(summary.total ?? 0)},
      // Omitted rather than zeroed when the backend stated no rate — see `statsToSummary`.
      ...(summary.presentPercent == null
        ? []
        : [{metric: 'Present %', value: `${summary.presentPercent}%`}]),
    ],
  }
}

function firstFiniteNumber(...values: unknown[]): number | null {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) return value
  }
  return null
}

/** Map `data.stats` (the backend's per-row roll-up) onto the summary shape the ring and bars already
 *  read. `present`/`late` are kept split so the ring can draw late as its own segment; `attended`
 *  (present + late) is left to callers that want late folded in. `unmarked` becomes `other`, and the
 *  total prefers marked days, then all rows, then the sum of the parts. */
function statsToSummary(stats: AttendanceStats): AttendanceSummary {
  const present = firstFiniteNumber(stats.present) ?? 0
  const absent = firstFiniteNumber(stats.absent) ?? 0
  const late = firstFiniteNumber(stats.late) ?? 0
  const leave = firstFiniteNumber(stats.leave) ?? 0
  const bunk = firstFiniteNumber(stats.bunk) ?? 0
  const other = firstFiniteNumber(stats.unmarked) ?? 0
  const total =
    firstFiniteNumber(stats.totalMarked, stats.totalRows) ??
    present + absent + late + leave + bunk + other
  return {
    present,
    absent,
    late,
    leave,
    bunk,
    other,
    total,
    // Null when the backend refused to state a rate, and it must stay null. A rate over
    // status-filtered rows is 100% by construction and a rate over a capped slice is not the group's
    // rate, so the backend sends none — `?? 0` turned that into "0% attendance" printed beside a bar
    // of 85 Present marks.
    presentPercent: firstFiniteNumber(stats.attendancePercent),
  }
}

/** Late is charted with the rest. Leaving it out made the bars add up to less than the answer beside
 *  them — 96 present + 13 absent + 3 leave = 112 for a day the text called 117 marked — and hid the
 *  five students the narration had just counted as attending.
 *
 *  Bunk is charted for the same reason, and was missing for a reason that no longer holds. The note
 *  here used to say it had to stay off because `statsToSummary` coerces an absent count to 0 and it
 *  would draw a permanent "Bunk 0" bar — but AttendanceStatusBars drops zero-count bars before it
 *  renders, so an absent status costs nothing. Leaving it out cost plenty: "8 students bunked
 *  Economics across 20 bunk records" drew Present 13, Late 4, Absent 1 and no Bunk bar at all, hiding
 *  the largest number in the answer. The ring drew it the whole time — the orange arc had no entry in
 *  the list beside it — so the chart and its own legend disagreed. */
const BAR_FIELDS: Array<{key: AttendanceStatusKey; label: string; field: string}> = [
  {key: 'present', label: 'Present', field: 'present'},
  {key: 'late', label: 'Late', field: 'late'},
  {key: 'absent', label: 'Absent', field: 'absent'},
  {key: 'leave', label: 'Leave', field: 'leave'},
  {key: 'bunk', label: 'Bunk', field: 'bunk'},
  // The ring charts only the marks the rate is taken over, so the people with no mark would
  // otherwise disappear from the visual entirely while the narration still counts them.
  {key: 'other', label: 'Unmarked', field: 'other'},
]

/** The backend's bucket keys are its own; these are ours. `unmarked` becomes `other` to match what
 *  `statsToSummary` already calls it, so the two paths cannot colour the same bar differently. */
const BUCKET_KEYS: Record<string, AttendanceStatusKey> = {
  present: 'present',
  late: 'late',
  absent: 'absent',
  leave: 'leave',
  bunk: 'bunk',
  checkout: 'checkout',
  unmarked: 'other',
}

/** `stats.buckets` is the backend's own answer to "what should be drawn": ordered as a reader expects
 *  them, pre-labelled, with zero buckets already dropped and `countsTowardRate` saying which ones the
 *  percentage was taken over. Reading it means a status this file has never heard of still reaches the
 *  chart — which is exactly how Bunk went missing, by being absent from a hand-maintained list while
 *  the payload carried it all along. */
function bucketBarSegments(stats: AttendanceStats): AttendanceStatusSegment[] | undefined {
  const buckets = stats.buckets
  if (!Array.isArray(buckets) || buckets.length === 0) return undefined

  const segments = buckets.flatMap((bucket) => {
    const count = firstFiniteNumber(bucket?.value)
    if (count == null) return []
    const label = bucket.label?.trim() || bucket.key
    return [
      {
        key: BUCKET_KEYS[bucket.key] ?? normalizeStatusKey(label),
        label,
        count,
        countsTowardRate: bucket.countsTowardRate ?? true,
      },
    ]
  })

  return segments.length > 0 ? segments : undefined
}

/** Bars for the statuses the backend actually measured. A status the query filtered out comes back
 *  null, and drawing it as `0` asserts something the data never said — "absent 3, present 0" for a
 *  question that only ever counted absences. `fullSummary` carries the unfiltered counts when the
 *  backend can supply them, so prefer it, then `summary`, then the pre-built bucket list, and fall
 *  back to mapping the raw stat fields only when there are no buckets to read. */
function buildBarSegments(
  data: AttendanceV2Payload['data']
): AttendanceStatusSegment[] | undefined {
  const explicit = (data?.fullSummary ?? data?.summary) as Record<string, unknown> | undefined
  if (!explicit && data?.stats) {
    const fromBuckets = bucketBarSegments(data.stats)
    if (fromBuckets) return fromBuckets
  }

  const statsSummary = data?.stats ? statsToSummary(data.stats) : undefined
  const source = (explicit ?? statsSummary) as Record<string, unknown> | undefined
  if (!source) return undefined

  const segments = BAR_FIELDS.flatMap(({key, label, field}) => {
    const count = firstFiniteNumber(source[field])
    return count == null ? [] : [{key, label, count}]
  })

  return segments.length > 0 ? segments : undefined
}

/** Build pie-chart segments from V2 payload summary counts and/or roster row statuses. */
export function buildV2AttendanceVisualSummary(
  payload: AttendanceV2Payload
): AttendanceVisualSummary | null {
  const data = payload.data
  // `data.stats` is the roster/per-row roll-up under the backend's own field names; normalize it into
  // a summary so the ring, bars and percentage read one source. Explicit `data.summary` still wins.
  const summary = data?.summary ?? (data?.stats ? statsToSummary(data.stats) : undefined)
  const {rows} = getV2ResultTableData(payload)

  // The figure the question asked for, not the size of the range it searched.
  const headlineValue = firstFiniteNumber(data?.headline?.value, data?.recordCount)
  const headlineLabel = data?.headline?.label?.trim() || null
  const barSegments = buildBarSegments(data)

  const decorate = (visual: AttendanceVisualSummary): AttendanceVisualSummary => ({
    ...visual,
    totalRecords: headlineValue ?? visual.totalRecords,
    headlineLabel,
    barSegments,
  })

  // `presentPercent` is present ÷ the statuses actually charted. When the query filtered to one
  // status that denominator is that status alone, so an "absences" answer reports 100% present off
  // 15 present days and nothing else. Charting the unfiltered counts restores a real denominator
  // and keeps the ring, the bars and the percentage telling the same story.
  const fullSegments = data?.fullSummary
    ? summaryToStatusSegments(data.fullSummary as AttendanceSummary)
    : []
  // Each branch below is the source that would be charted; when that source holds no actual mark
  // there is no chart, and no falling through to a weaker source that could only say the same.
  if (fullSegments.length > 0) {
    if (!hasMarkedSegments(fullSegments)) return null
    return decorate(
      buildVisualFromSegments(
        fullSegments,
        fullSegments.reduce((sum, s) => sum + s.count, 0)
      )
    )
  }

  if (summary) {
    const fromSummary = summaryToStatusSegments(summary)
    if (fromSummary.length > 0) {
      if (!hasMarkedSegments(fromSummary)) return null
      return decorate(
        buildVisualFromSegments(
          fromSummary,
          summary.total ?? fromSummary.reduce((sum, s) => sum + s.count, 0),
          summary.presentPercent
        )
      )
    }
  }

  const fromRows = buildStatusSegmentsFromRows(rows, 'status')
  if (fromRows.length > 0) {
    if (!hasMarkedSegments(fromRows)) return null
    return decorate(buildVisualFromSegments(fromRows, rows.length))
  }

  if (summary && (summary.total ?? 0) > 0) {
    if (rows.length === 1 && !hasMeaningfulStatusCounts(summary)) {
      return null
    }
    const totalLooksLikeRowCount =
      rows.length > 0 && summary.total === rows.length && !hasMeaningfulStatusCounts(summary)
    if (totalLooksLikeRowCount) {
      return null
    }
    return decorate(v2SummaryToVisualSummary(summary))
  }

  return null
}

export function hasV2AttendanceChart(summary: AttendanceVisualSummary | null): boolean {
  return Boolean(summary && hasMarkedSegments(summary.statusSegments))
}
